// PageDescription: 통합검색 페이지
import { QueryClient, dehydrate } from '@tanstack/react-query';
import { GetServerSideProps } from 'next';
import { NextSeo } from 'next-seo';

import DefaultLayout from '@/components/layouts/DefaultLayout';
import HomeSearchTemplate from '@/components/templates/mobile/home/home-search-template';
import useGetBannerAdExchange from '@/hooks/advertisement/useGetBannerAdExchange';
import {
  useGetDoctorsByKeywordQuery,
  useGetKeywordSearchMagazineQuery,
  useGetkeywordSearchQnaQuery,
} from '@/hooks/search/search.gql.impl';
import { AdPageType } from '@/models/ad/list';
import { CustomPageProps } from '@/types/app';
import { ROUTES } from '@/types/common';
import {
  BoardSearchResultVo,
  DoctorSearchResultVo,
  MagazineSearchResultVo,
  QuestionSearchResultVo,
  SearchResult,
} from '@/types/search';
import { withClientServerSideProps } from '@/utils/mobile/RNPlugins';
import { addQueryString } from '@/utils/url';
import { validateNonSpecialPattern } from '@/utils/validate';

const DEFAULT_IMAGE_HEIGHT = 600;
const DEFAULT_IMAGE_WIDTH = 315;

enum EPageType {
  SYMPTOM_SEARCH,
  SEARCH_RESULT,
  INTEGRATED_SEARCH,
}

const getPageType = (entryType: string, keyword: string) => {
  if (entryType === 'symptom') {
    return EPageType.SYMPTOM_SEARCH;
  }
  if (keyword) {
    return EPageType.SEARCH_RESULT;
  }
  return EPageType.INTEGRATED_SEARCH;
};

const getSeoContents = (pageType: EPageType, keyword: string) => {
  if (pageType === EPageType.SEARCH_RESULT) {
    return {
      title: `${keyword} 검색결과 - 닥터나우`,
      description: '증상, 질병, 병원, 의료진 Q&A 등 필요한 모든 정보를 확인해 보세요.',
      imageUrl: process.env.NEXT_PUBLIC_SEARCH_RESULT_OG_IMAGE ?? 'https://media.doctornow.co.kr/og-searchresult.png',
    };
  }

  return {
    title: '증상검색 - 닥터나우',
    description: '증상만 검색해도 내가 알고 싶은 정보를 쉽고 빠르게 찾을 수 있어요.',
    imageUrl: process.env.NEXT_PUBLIC_SYMPTOM_SEARCH_OG_IMAGE ?? 'https://media.doctornow.co.kr/og-symptomsearch.png',
  };
};

interface Props extends CustomPageProps {
  searchResultData: {
    boardPostData: SearchResult<BoardSearchResultVo>;
    magazineData: SearchResult<MagazineSearchResultVo>;
    questionData: SearchResult<QuestionSearchResultVo>;
    doctorData: SearchResult<DoctorSearchResultVo>;
    keyword: string;
  };
  // TODO entryType은 enum으로 대체되어야 함
  entryType: string;
  keyword: string;
}

const SearchPage = (props: Props) => {
  const { entryType, client, keyword } = props;
  const { title, description, imageUrl } = getSeoContents(getPageType(entryType, keyword), keyword);

  return (
    <>
      <NextSeo
        title={title}
        description={description}
        canonical={`${process.env.NEXT_PUBLIC_DOMAIN}${ROUTES.SEARCH('').HOME.path}${addQueryString({ keyword })}`}
        openGraph={{
          type: 'website',
          title: title,
          description: description,
          locale: 'ko-KR',
          siteName: '닥터나우',
          images: [
            {
              url: imageUrl,
              width: DEFAULT_IMAGE_HEIGHT,
              height: DEFAULT_IMAGE_WIDTH,
              alt: title,
            },
          ],
          defaultImageHeight: DEFAULT_IMAGE_HEIGHT,
          defaultImageWidth: DEFAULT_IMAGE_WIDTH,
        }}
      />
      <HomeSearchTemplate entryType={entryType} client={client} />
    </>
  );
};

SearchPage.Layout = DefaultLayout;
export default SearchPage;

export const getServerSideProps: GetServerSideProps = withClientServerSideProps(async (ctx) => {
  const SECTION_RESULT_SIZE = 4;

  const queryClient = new QueryClient();

  const keyword = String(ctx.query.keyword ?? '');

  if (!validateNonSpecialPattern(keyword)) {
    return {
      redirect: {
        permanent: true,
        destination: ROUTES.SEARCH('').HOME.path,
      },
    };
  }

  const variables: { keyword: string; size: number } = {
    keyword: String(ctx.query.keyword) ?? '',
    size: SECTION_RESULT_SIZE,
  };

  await Promise.allSettled([
    useGetBannerAdExchange.prefetchQuery(queryClient, AdPageType.Search, ctx.client),
    queryClient.prefetchQuery(
      useGetkeywordSearchQnaQuery.getKey({ request: { ...variables } }),
      useGetkeywordSearchQnaQuery.fetcher({ request: { ...variables } })
    ),
    queryClient.prefetchQuery(
      useGetKeywordSearchMagazineQuery.getKey({ request: { ...variables } }),
      useGetKeywordSearchMagazineQuery.fetcher({ request: { ...variables } })
    ),
    queryClient.prefetchQuery(
      useGetDoctorsByKeywordQuery.getKey({
        request: { ...variables },
      }),
      useGetDoctorsByKeywordQuery.fetcher({
        request: { ...variables },
      })
    ),
  ]);

  return {
    props: {
      keyword: ctx.query.keyword ?? '',
      type: ctx.query.type ?? '',
      entryType: ctx.query.entryType ?? '',
      dehydratedState: dehydrate(queryClient),
    },
  };
});
